import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { apiOptionsToDropdownItems, capitalizeWords, logger, numberFormat } from 'shared/lib'
import { type IDropdownItem, toastStore } from 'shared/ui'
import { BillingApi } from 'entities/Billing'
import { AdminCustomPriceApi } from 'entities/Admin/customPrices'
import { type IPlanFormatted } from 'entities/Admin/customPrices/store/types'
import { BillingPlan } from 'entities/Billing/model/BillingPlan'
import { BillingCustomPlan } from 'entities/Billing/model/BillingCustomPlan'

export class AdminOrganizationPlanStore {
  private _planLoading = false
  private _originalPlansMap: Map<string, BillingPlan> = new Map()
  private _customPlansMap: Map<number, BillingCustomPlan> = new Map()

  constructor() {
    makeAutoObservable(this)
  }

  get originalPlans() {
    return Array.from(this._originalPlansMap.values())
  }

  get customPlans() {
    return Array.from(this._customPlansMap.values())
  }

  get planLoading() {
    return this._planLoading
  }

  get customPricesItems(): IDropdownItem[] {
    return apiOptionsToDropdownItems(this.customPlans)
  }

  loadCustomPrice = async () => {
    try {
      if (this._customPlansMap.size) return
      const { data } = await AdminCustomPriceApi.getCustomPrices()

      runInAction(() => {
        data.data.forEach((item) => {
          this._customPlansMap.set(item.id, new BillingCustomPlan(item))
        })
      })
    } catch (e) {
      logger.error(e)
    }
  }

  getCustomPriceLabelById = (id?: number | null) => {
    if (!id) return '–'
    return this._customPlansMap.get(id)?.label || '–'
  }

  selectedPlanData = (selectedPlan?: string | number | null): IPlanFormatted | null => {
    if (!selectedPlan) return null

    const customPlan = this.customPlans.find((plan) => {
      return selectedPlan === plan.id
    })

    const contractTermNameCustom = this.getContractTermName(customPlan?.interval || null)

    if (customPlan) {
      return {
        key: customPlan.id,
        planType: 'custom',
        name: customPlan.label,
        contract_term: customPlan.contract_term || customPlan.interval,
        interval: customPlan.interval,
        credits: customPlan.credits,
        total: customPlan.price_dollars,
        additional_credit_amount: customPlan.additional_credit_price,
        free_seats: customPlan.seats,
        free_numbers: customPlan.numbers,
        stripe_id: customPlan.stripe_id,
        percent: customPlan.percent,
        type: customPlan.type,
        contractTermName: contractTermNameCustom,
      }
    }

    const originalPrice = this.originalPlans.find((plan) => {
      return selectedPlan === plan.id
    })

    if (originalPrice) {
      const planName = capitalizeWords(originalPrice.id.replaceAll('-', ' '))
      const contractTermNameRegular = this.getContractTermName(originalPrice?.interval || null)

      return {
        key: originalPrice.id,
        planType: 'pro',
        name: planName,
        contract_term: originalPrice.interval,
        interval: originalPrice.interval,
        credits: originalPrice.credits,
        total: originalPrice.price_dollars,
        additional_credit_amount: originalPrice.additional_credit_price,
        free_seats: 0,
        free_numbers: 0,
        stripe_id: originalPrice.id,
        percent: null,
        type: null,
        contractTermName: contractTermNameRegular,
      }
    }

    return null
  }

  private _fetchCustomPlans = async () => {
    try {
      const customPriceResponse = await AdminCustomPriceApi.getCustomPrices()

      runInAction(() => {
        customPriceResponse.data.data.forEach((item) => {
          this._customPlansMap.set(item.id, new BillingCustomPlan(item))
        })
      })
    } catch (e) {
      if (e instanceof AxiosError) {
        toastStore.add({
          title: `API: ${e.config?.url} - Message: ${
            e.errors.getError('message') || e.errors.getError('default')
          }`,
          type: 'error',
        })
      }
    }
  }

  private _fetchRegularPlans = async () => {
    try {
      const plansResponse = await BillingApi.getPlans()

      const originalPlansResponse = [...plansResponse.data.annual, ...plansResponse.data.monthly]

      runInAction(() => {
        originalPlansResponse.forEach((item) => {
          this._originalPlansMap.set(item.id, new BillingPlan(item))
        })
      })
    } catch (e) {
      logger.error(e)
    }
  }

  fetchPlans = async () => {
    try {
      runInAction(() => {
        this._planLoading = true
      })

      await Promise.all([this._fetchRegularPlans(), this._fetchCustomPlans()])
    } catch (e) {
      console.error('error', e)
    } finally {
      runInAction(() => {
        this._planLoading = false
      })
    }
  }

  getContractTermName = (interval: string | null) => {
    if (interval === null || interval === 'monthly') {
      return 'month'
    }

    if (interval === 'quarterly') {
      return 'quarter'
    }

    if (interval === 'semi-annual') {
      return '6 months'
    }

    if (interval === 'annual') {
      return 'year'
    }

    return interval
  }

  get convertedPlansToOptions() {
    const customPlanOptions = this.customPlans.map((plan) => ({
      ...plan,
      id: plan.id,
      disabled: plan.status === 'draft',
      label: `${plan.id} ${plan.label} ${numberFormat({
        value: plan.price_dollars,
        currency: 'USD',
      })} ${plan.interval}`,
    }))

    const originalPlanOptions = this.originalPlans.map((plan) => {
      const planName = capitalizeWords(plan.id.replaceAll('-', ' '))
      const price = numberFormat({ value: plan.price_dollars, currency: 'USD' })

      return {
        ...plan,
        label: `${plan.id} ${planName} ${price} ${plan.interval}`,
      }
    })

    return [...customPlanOptions, ...originalPlanOptions]
  }

  get convertedFreePlansToOptions() {
    return this.customPlans
      .map((plan) => ({
        ...plan,
        id: plan.id,
        disabled: plan.status === 'draft',
        label: `${plan.id} ${plan.label} ${numberFormat({
          value: plan.price_dollars,
          currency: 'USD',
        })} ${plan.interval}`,
      }))
      .filter((item) => item.is_enterprise_trial_available)
  }
}
